/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Badge, Icon, Tooltip } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import React from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import { KPI_PERCENTAGE_DECIMAL_PRECISION } from '../../../shared/Utils/Constants';
import { getNullToZero } from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { getSummaryBadgeColor } from '../../../shared/Utils/Graph/AllGraphUtils';
import {
  formatCurrency,
  formatPriceValue,
  formatUnit
} from '../../../shared/Utils/Utils';
import { config } from '../constants';
import { IEnergyOptimizationEstimatedSavings } from '../EnergyOptimizationInterfaces';

const d = descriptors.energyManagement;
export interface IEnergyOptimizationSavingsSummaryProps {
  currencyName?: string;
  estimatedSavings?: IEnergyOptimizationEstimatedSavings;
  operationalTimePercentage?: number;
}

const EnergyOptimizationSavingsSummary = ({
  currencyName = config.defaultCurrencyName,
  estimatedSavings = { monetaryValue: 0, decimalPercentage: 0 },
  operationalTimePercentage = 0
}: IEnergyOptimizationSavingsSummaryProps) => {
  const [t] = useTranslation();
  const cumulativeSummary = (
    <div
      id="cumulativeSummary"
      className="energy-optimization-summary-cumulative"
    >
      <span>
        {formatPriceValue(
          formatCurrency(estimatedSavings.monetaryValue!, currencyName, false)
        )}
      </span>
      <span className="emaas-summary-unit">
        {formatUnit(
          formatCurrency(estimatedSavings.monetaryValue!, currencyName)
        )}
      </span>
    </div>
  );
  let cssSavingPercentage = 'saving-percentage-negative';
  if (estimatedSavings.decimalPercentage >= 0) {
    cssSavingPercentage = 'saving-percentage-positive';
  }

  return (
    <div className="energy-optimization-summary-container">
      <div className="energy-optimization-summary-items">
        {cumulativeSummary}
        <div className="percentage" id="percentageSummary">
          <span className={`${cssSavingPercentage}`}>
            {' '}
            <Badge
              color={getSummaryBadgeColor(estimatedSavings.decimalPercentage)}
              empty
            />
            {Math.abs(
              parseFloat(
                getNullToZero(estimatedSavings.decimalPercentage).toFixed(
                  KPI_PERCENTAGE_DECIMAL_PRECISION
                )
              )
            )}
            %
          </span>
        </div>
      </div>
      <div className="eo-estimated-saving" id="operationalTimePer">
        {t(d.savingsSummaryEO, {
          opertionalPercentage: parseFloat(
            getNullToZero(operationalTimePercentage).toFixed(
              KPI_PERCENTAGE_DECIMAL_PRECISION
            )
          )
        })}
        <Tooltip
          element={<Icon root="common" name="badge-info" size="small" />}
          event="hover"
          content={t(d.EOSAVINGTOOLTIP)}
          position="right center"
          size="small"
        />
      </div>
    </div>
  );
};

export default EnergyOptimizationSavingsSummary;
